import { useCallback, useEffect, useMemo, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  resolveStudioAgentAlias,
  studioThreadNavigationHost,
} from "@/components/studio/studioThreadUtils";
import {
  builderCalistoHref,
  isStudioThreadSurface,
  readContextTargetId,
} from "@/utils/contextUrl";
import {
  isLeafNavigationLocked,
  lockLeafNavigation,
} from "@/utils/leafNavigationLock";
import { toast } from "@/providers/NotificationProvider";
import { AGENT_SPEC_PULL_QUERY_KEY } from "@/hooks/invalidateAgentSpecResources";
import { agentSpecsAPI } from "@/services/api/agentSpecs";
import { useAgentSpecStudioStore } from "@/store/agentSpecStudio";
import { findContextDeployment, useContextStore } from "@/store/context";
import {
  isChiefOfStaffDeployment,
  resolveStudioSurfaceHostId,
  shouldClearStudioThreadOnHostChange,
} from "@/utils/instanceRole";
import { usePermissionStore } from "@/store/permissions";

export type StudioFromGoalDraft = {
  title: string;
  description?: string;
};

/** Query keys scoped to a Studio thread — drop on Agent switch. */
const THREAD_SCOPED_SEARCH_KEYS = [
  "threadId",
  "revisionId",
  "planId",
  "continuePreview",
  "section",
  "resourceId",
] as const;

export function studioThreadsQueryKey(
  organizationId: string | null | undefined,
  agentId: string | null | undefined,
) {
  return ["agent-spec-studio-threads", organizationId, agentId] as const;
}

function scopedStudioKey(
  organizationId: string | null | undefined,
  agentId: string | null | undefined,
) {
  return `${organizationId ?? ""}:${agentId ?? ""}`;
}

export function useAgentSpecStudioThreads(options?: {
  /** When true, keep URL `threadId` and store selection in sync. */
  syncSelection?: boolean;
  /** Override the store Agent so each tree branch can list its own fils. */
  agentId?: string | null;
}) {
  const syncSelection = options?.syncSelection ?? false;
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const organizationId = useContextStore((state) => state.org);
  const storeAgentId = useContextStore((state) => state.deploymentId);
  const chiefOfStaffId = useContextStore(
    (state) => state.chiefOfStaff?.id ?? null,
  );
  const urlAgentId = readContextTargetId(location.pathname, searchParams);
  const agentId = resolveStudioSurfaceHostId({
    overrideAgentId: options?.agentId,
    pathname: location.pathname,
    urlAgentId,
    chiefOfStaffId,
    storeAgentId,
  });
  const environmentName = useContextStore((state) => state.env);
  const applicationKey = searchParams.get("applicationKey");
  const canWriteRecipes = usePermissionStore((state) =>
    state.hasCapability("write:recipes"),
  );
  const selectedAgent = useContextStore((state) =>
    findContextDeployment(state, agentId),
  );
  const selectedThreadId = useAgentSpecStudioStore(
    (state) => state.selectedThreadId,
  );
  const turnPending = useAgentSpecStudioStore((state) => state.turnPending);
  const pendingThreadSelectionId = useAgentSpecStudioStore(
    (state) => state.pendingThreadSelectionId,
  );
  const setSelectedThreadId = useAgentSpecStudioStore(
    (state) => state.setSelectedThreadId,
  );
  const setPendingThreadSelectionId = useAgentSpecStudioStore(
    (state) => state.setPendingThreadSelectionId,
  );
  const setStudioError = useAgentSpecStudioStore((state) => state.setError);
  const requestedThreadId = searchParams.get("threadId");
  const studioScopeKey = scopedStudioKey(organizationId, agentId);
  const previousStudioScopeKeyRef = useRef(studioScopeKey);
  const previousAgentIdRef = useRef(agentId);
  const fallbackAlias = resolveStudioAgentAlias(
    selectedAgent?.name,
    agentId ?? "agent",
  );
  const threadsQueryKey = studioThreadsQueryKey(organizationId, agentId);

  const pullQuery = useQuery({
    queryKey: [
      AGENT_SPEC_PULL_QUERY_KEY,
      organizationId,
      agentId,
      environmentName,
    ],
    queryFn: () =>
      agentSpecsAPI.pull(organizationId!, agentId!, {
        environmentName: environmentName ?? undefined,
      }),
    enabled: Boolean(canWriteRecipes && organizationId && agentId),
    retry: false,
    staleTime: 60_000,
  });

  const threadsQuery = useQuery({
    queryKey: threadsQueryKey,
    queryFn: () => agentSpecsAPI.listStudioThreads(organizationId!, agentId!),
    enabled: Boolean(canWriteRecipes && organizationId && agentId),
    retry: false,
    refetchInterval: (query) => {
      const listed = query.state.data?.threads ?? [];
      if (listed.some((thread) => thread.attention === "running")) {
        return 2_000;
      }
      if (turnPending) {
        return 2_000;
      }
      return false;
    },
  });

  const agentAlias = pullQuery.data?.agent_alias ?? fallbackAlias;
  const threads = useMemo(
    () => threadsQuery.data?.threads ?? [],
    [threadsQuery.data],
  );

  const applyThreadSelection = useCallback(
    (
      threadId: string,
      revisionId?: string | null,
      options?: {
        navigateToChat?: boolean;
        applicationKey?: string | null;
        hostAgentId?: string | null;
      },
    ) => {
      setSelectedThreadId(threadId);
      setStudioError(null);
      const livePath = window.location.pathname;
      const liveSearch = window.location.search;
      const scopeKey = options?.applicationKey ?? applicationKey;
      // Explicit New thread / fil click must still open chat during a leaf lock.
      // Auto-sync never passes navigateToChat.
      if (isLeafNavigationLocked() && !options?.navigateToChat) {
        return;
      }
      // Explicit New thread / fil click always opens chat. Auto-sync must not
      // pass navigateToChat after the user left Calisto (Application review).
      if (options?.navigateToChat) {
        lockLeafNavigation();
        const host = studioThreadNavigationHost({
          listingAgentId: options.hostAgentId || agentId,
          urlAgentId: readContextTargetId(
            livePath,
            new URLSearchParams(liveSearch),
          ),
        });
        navigate(
          builderCalistoHref(organizationId, host, {
            threadId,
            revisionId,
            applicationKey: scopeKey,
          }),
        );
        return;
      }
      if (!isStudioThreadSurface(livePath)) {
        return;
      }
      const next = new URLSearchParams(window.location.search);
      if (agentId) {
        next.set("agentId", agentId);
        next.set("deploymentId", agentId);
      }
      next.set("threadId", threadId);
      if (revisionId) next.set("revisionId", revisionId);
      else next.delete("revisionId");
      setSearchParams(next, { replace: true });
    },
    [
      agentId,
      applicationKey,
      navigate,
      organizationId,
      setSearchParams,
      setSelectedThreadId,
      setStudioError,
    ],
  );

  const clearThreadScopedSearch = useCallback(() => {
    if (isLeafNavigationLocked()) {
      return;
    }
    if (!isStudioThreadSurface(window.location.pathname)) {
      return;
    }
    const next = new URLSearchParams(window.location.search);
    let changed = false;
    for (const key of THREAD_SCOPED_SEARCH_KEYS) {
      if (next.has(key)) {
        next.delete(key);
        changed = true;
      }
    }
    if (!changed) {
      return;
    }
    setSearchParams(next, { replace: true });
  }, [setSearchParams]);

  // Selecting another Agent (sidebar tree) updates deploymentId while the URL
  // still points at the previous Agent's thread → 404. Drop thread-scoped
  // params as soon as the Studio scope changes.
  useEffect(() => {
    if (!syncSelection) {
      previousStudioScopeKeyRef.current = studioScopeKey;
      previousAgentIdRef.current = agentId;
      return;
    }
    if (previousStudioScopeKeyRef.current === studioScopeKey) {
      return;
    }
    const previousAgentId = previousAgentIdRef.current;
    previousStudioScopeKeyRef.current = studioScopeKey;
    if (!agentId?.trim()) {
      return;
    }
    previousAgentIdRef.current = agentId;
    if (
      !shouldClearStudioThreadOnHostChange({
        previousAgentId,
        nextAgentId: agentId,
      })
    ) {
      return;
    }
    // A fil click already locked navigation and wrote the target URL. Do not
    // wipe that selection while the router still shows the previous Agent.
    if (isLeafNavigationLocked()) {
      return;
    }
    setPendingThreadSelectionId(null);
    setSelectedThreadId(null);
    setStudioError(null);
    clearThreadScopedSearch();
  }, [
    agentId,
    clearThreadScopedSearch,
    setPendingThreadSelectionId,
    setSelectedThreadId,
    setStudioError,
    studioScopeKey,
    syncSelection,
  ]);

  useEffect(() => {
    if (!syncSelection) return;
    // Wait for this Agent's thread list before trusting URL threadId — otherwise
    // a previous Agent's threadId triggers getStudioThread → 404.
    if (!threadsQuery.isSuccess) return;

    // Shared across hook instances: the sidebar selects or creates threads
    // without syncSelection, but this panel must not clobber that intent while
    // router navigation still exposes the previous URL.
    if (pendingThreadSelectionId) {
      if (
        selectedThreadId !== pendingThreadSelectionId ||
        requestedThreadId !== pendingThreadSelectionId
      ) {
        if (
          !isStudioThreadSurface(window.location.pathname) ||
          isLeafNavigationLocked()
        ) {
          setSelectedThreadId(pendingThreadSelectionId);
          return;
        }
        const pendingThread = threads.find(
          (thread) => thread.id === pendingThreadSelectionId,
        );
        applyThreadSelection(
          pendingThreadSelectionId,
          pendingThread?.active_revision_id ?? null,
        );
        return;
      }
      if (
        threads.some((thread) => thread.id === pendingThreadSelectionId)
      ) {
        setPendingThreadSelectionId(null);
      }
      return;
    }

    const requested = threads.find((thread) => thread.id === requestedThreadId);
    const current = threads.find((thread) => thread.id === selectedThreadId);

    // URL fil is not on this host. Wait for the list; then adopt this host's
    // fil instead of calling getStudioThread with a foreign id (404).
    if (requestedThreadId && !requested) {
      if (!threadsQuery.isSuccess) return;
      if (threads.length === 0) {
        setSelectedThreadId(null);
        return;
      }
      applyThreadSelection(threads[0].id, threads[0].active_revision_id);
      return;
    }

    // Store selection left over from another Agent.
    if (selectedThreadId && !current && threads.length > 0) {
      applyThreadSelection(threads[0].id, threads[0].active_revision_id);
      return;
    }

    // Confirmed URL threadId for this Agent.
    if (requested && requestedThreadId !== selectedThreadId) {
      applyThreadSelection(
        requestedThreadId!,
        requested.active_revision_id ?? null,
      );
      return;
    }

    if (threads.length === 0) return;

    const next = current ?? requested ?? threads[0];
    if (next.id === selectedThreadId && requestedThreadId === next.id) return;
    if (next.id === selectedThreadId && !requestedThreadId) {
      applyThreadSelection(next.id, next.active_revision_id);
      return;
    }
    if (next.id === selectedThreadId) return;
    applyThreadSelection(next.id, next.active_revision_id);
  }, [
    applyThreadSelection,
    clearThreadScopedSearch,
    pendingThreadSelectionId,
    requestedThreadId,
    selectedThreadId,
    setPendingThreadSelectionId,
    setSelectedThreadId,
    syncSelection,
    threads,
    threadsQuery.isSuccess,
  ]);

  type CreateThreadInput = {
    hostAgentId?: string;
    environmentName?: string;
    applicationKey?: string;
    title?: string;
    silent?: boolean;
    goal?: StudioFromGoalDraft;
  };

  const createThreadMutation = useMutation({
    mutationFn: async (input?: CreateThreadInput) => {
      const host = input?.hostAgentId || agentId;
      if (!organizationId || !host) {
        throw new Error("organization and host agent are required");
      }
      const env = input?.environmentName ?? environmentName ?? undefined;
      if (input?.goal?.title) {
        return agentSpecsAPI.createStudioJobFromGoal(organizationId, host, {
          agent_alias: agentAlias,
          environment_name: env,
          application_key: input.applicationKey,
          goal: {
            title: input.goal.title,
            description: input.goal.description,
            source: "api",
          },
        });
      }
      return agentSpecsAPI.createStudioThread(organizationId, host, {
        agent_alias: agentAlias,
        environment_name: env,
        application_key: input?.applicationKey,
        title: input?.title,
      });
    },
    onSuccess: async (created, input) => {
      const createdThread = created.thread;
      const revisionId =
        "revision" in created && created.revision && typeof created.revision === "object"
          ? (created.revision as { id?: string }).id
          : createdThread.active_revision_id;
      queryClient.setQueryData(
        [
          "agent-spec-studio-thread",
          organizationId,
          createdThread.agent_id || agentId,
          createdThread.id,
        ],
        created,
      );
      setPendingThreadSelectionId(createdThread.id);
      applyThreadSelection(createdThread.id, revisionId ?? null, {
        navigateToChat: true,
        applicationKey: input?.applicationKey,
        hostAgentId: createdThread.agent_id || input?.hostAgentId || agentId,
      });
      await queryClient.invalidateQueries({ queryKey: threadsQueryKey });
      if (!input?.silent) {
        toast.success(
          "goal" in created
            ? t("agentSpec.studioFromGoalSuccess")
            : t("agentSpec.studioCreateThreadSuccess"),
        );
      }
    },
    onError: (error) => {
      const message =
        error instanceof Error ? error.message : String(error);
      setStudioError(message);
      toast.error(message || t("agentSpec.studioError"));
    },
  });

  const createFromGoalMutation = useMutation({
    mutationFn: (goal: StudioFromGoalDraft) =>
      agentSpecsAPI.createStudioJobFromGoal(organizationId!, agentId!, {
        agent_alias: agentAlias,
        environment_name: environmentName ?? undefined,
        goal: {
          title: goal.title,
          description: goal.description,
          source: "api",
        },
      }),
    onSuccess: async (created) => {
      setPendingThreadSelectionId(created.thread.id);
      // Set store + URL together before list refetch so sync cannot clobber.
      applyThreadSelection(
        created.thread.id,
        created.thread.active_revision_id,
        { navigateToChat: true },
      );
      await queryClient.invalidateQueries({ queryKey: threadsQueryKey });
      await queryClient.invalidateQueries({
        queryKey: [
          "agent-spec-studio-thread",
          organizationId,
          agentId,
          created.thread.id,
        ],
      });
      toast.success(t("agentSpec.studioFromGoalSuccess"));
    },
    onError: (error) => {
      const message =
        error instanceof Error ? error.message : String(error);
      setStudioError(message);
      toast.error(message || t("agentSpec.studioError"));
    },
  });

  const welcomeCreatedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!syncSelection) return;
    if (!threadsQuery.isSuccess || threads.length > 0) return;
    if (requestedThreadId) return;
    if (!organizationId || !agentId || !canWriteRecipes) return;
    if (!isChiefOfStaffDeployment(selectedAgent ?? {})) return;
    if (createThreadMutation.isPending || createFromGoalMutation.isPending) {
      return;
    }
    const key = scopedStudioKey(organizationId, agentId);
    if (welcomeCreatedKeyRef.current === key) return;
    welcomeCreatedKeyRef.current = key;
    createThreadMutation.mutate({
      hostAgentId: agentId,
      title: t("agentSpec.studioChiefOfStaffWelcomeTitle"),
      silent: true,
    });
  }, [
    agentId,
    canWriteRecipes,
    createFromGoalMutation.isPending,
    createThreadMutation,
    organizationId,
    requestedThreadId,
    selectedAgent,
    syncSelection,
    t,
    threads.length,
    threadsQuery.isSuccess,
  ]);

  const selectThread = useCallback(
    (threadId: string) => {
      const thread = threads.find((item) => item.id === threadId);
      setPendingThreadSelectionId(threadId);
      applyThreadSelection(threadId, thread?.active_revision_id, {
        navigateToChat: true,
        hostAgentId: thread?.agent_id || agentId,
      });
    },
    [agentId, applyThreadSelection, setPendingThreadSelectionId, threads],
  );

  const createThread = useCallback((input?: CreateThreadInput) => {
    const host = input?.hostAgentId || agentId;
    if (
      !organizationId ||
      !host ||
      createThreadMutation.isPending ||
      createFromGoalMutation.isPending
    ) {
      return;
    }
    createThreadMutation.mutate(input);
  }, [
    agentId,
    createFromGoalMutation.isPending,
    createThreadMutation,
    organizationId,
  ]);

  const createThreadFromGoal = useCallback(
    (
      goal: StudioFromGoalDraft,
      options?: { onSuccess?: () => void },
    ) => {
      if (
        !organizationId ||
        !agentId ||
        createThreadMutation.isPending ||
        createFromGoalMutation.isPending
      ) {
        return;
      }
      createFromGoalMutation.mutate(goal, {
        onSuccess: () => options?.onSuccess?.(),
      });
    },
    [
      agentId,
      createFromGoalMutation,
      createThreadMutation.isPending,
      organizationId,
    ],
  );

  const listError =
    threadsQuery.error instanceof Error
      ? threadsQuery.error.message
      : threadsQuery.error
        ? String(threadsQuery.error)
        : null;

  return {
    organizationId,
    agentId,
    canWriteRecipes,
    agentAlias,
    threads,
    threadsQuery,
    pullQuery,
    selectedThreadId,
    createThread,
    createThreadFromGoal,
    createThreadPending: createThreadMutation.isPending,
    createFromGoalPending: createFromGoalMutation.isPending,
    selectThread,
    listError,
    threadsQueryKey,
  };
}
